/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import { parse as parseQuery } from 'qs';
import Helmet from 'react-helmet';
import { size, flow, pickBy, keys, identity, get } from 'lodash/fp';
import styled from '@emotion/styled';
import Tappable from 'app/components/commons/Tappable';
import t from 'app/utils/translate';
import Avatar from 'app/components/commons/Avatar';
import * as userActions from 'app/actions/userActions';
import AvatarChooser from 'app/components/commons/AvatarChooser';
import Button from 'app/components/commons/Button';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import Modal from 'app/components/commons/Modal';
import NotificationHandler from 'app/components/notifications/NotificationHandler';
import ProfileForm from 'app/components/user/ProfileForm';
import Responsive from 'app/components/responsive/Responsive';
import VerificationHeadline from 'app/components/user/VerificationHeadline';
import VerificationSummary from 'app/components/user/VerificationSummary';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import NightModeButton from '../../components/commons/NightModeButton';

type Props = {
  avatar: Object,
  darkMode: boolean,
  emailVerified: boolean,
  enableMobileVerification: boolean,
  errors?: Array<*>,
  firstName?: string,
  getCurrentUser: () => void,
  identityVerified: boolean,
  location: Object,
  lastName?: string,
  onClearError: () => void,
  onUpdate: Object => void,
  phoneVerified: boolean,
  profileCompleted: boolean,
  updatingUser: boolean,
  userId: number,
  username?: string
};

type State = {
  isAvatarUpdating: boolean,
  showAvatarModal: boolean
};

const AvatarContainer = styled.div(({ theme }) => ({
  display: 'flex',
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  [theme.mediaQuery.desktopAndUp]: {
    justifyContent: 'center',
    marginBottom: '1rem'
  }
}));

const AvatarBox = styled.div({
  cursor: 'pointer'
});

const Bottom = styled.div({
  marginTop: 'auto'
});

const ProfileMain = styled.div(({ theme }) => ({
  marginTop: 20,
  padding: 10,
  [theme.mediaQuery.desktopAndUp]: {
    display: 'flex',
    marginTop: 25,
    padding: '1.5rem'
  }
}));

const InfoPane = styled.div(({ theme }) => ({
  paddingBottom: 20,
  [theme.mediaQuery.desktopAndUp]: {
    borderRightColor: theme.color.basis3,
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    display: 'flex',
    flexBasis: 'auto',
    flexDirection: 'column',
    flexGrow: 0,
    flexShrink: 0,
    marginRight: '1.5rem',
    paddingBottom: 0,
    paddingRight: '1.5rem'
  }
}));

const FormPane = styled.div(({ theme }) => ({
  [theme.mediaQuery.desktopAndUp]: {
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1
  }
}));

const InfoTop = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  paddingBottom: '1rem',
  [theme.mediaQuery.desktopAndUp]: {
    display: 'block'
  }
}));

const UserDetails = styled.div(({ theme }) => ({
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  [theme.mediaQuery.desktopAndUp]: {
    paddingBottom: '1rem',
    textAlign: 'center'
  }
}));

const NameDisplay = styled.div({
  fontSize: '1.125rem'
});

const UsernameDisplay = styled.div(({ darkMode, theme }) => ({
  color: darkMode ? theme.color.basis5 : theme.color.invertableBasis5,
  fontSize: '0.8rem'
}));

const VerificationHeadlineWrapper = styled.div({
  flexBasis: 0,
  flexGrow: 2,
  flexShrink: 2
});

const VerificationHeadlineInner = styled.div(({ theme }) => ({
  padding: '0.75rem 1.5rem',
  [theme.mediaQuery.desktopAndUp]: {
    padding: 0
  }
}));

const VerificationSummaryWrapper = styled.div({
  flexBasis: 0,
  flexGrow: 2,
  flexShrink: 2
});

const WarningMessage = styled.div(({ darkMode, theme }) => ({
  fontSize: theme.fontSize.tiny,
  color: darkMode ? theme.color.basis6 : theme.color.invertableBasis6,
  width: 150,
  paddingBottom: 2
}));

const StyledNightModeButton = styled(NightModeButton)(
  ({ theme }) => ({
    borderRadius: 4,
    padding: '4px 8px',
    fontSize: theme.fontSize.small
  }),
  ({ theme, selected }) =>
    selected
      ? {
          color: theme.color.white,
          backgroundColor: theme.color.basis3
        }
      : {
          color: theme.color.basis3,
          backgroundColor: theme.color.basis5
        }
);

class AccountProfile extends React.Component<Props, State> {
  props: Props;

  state: State = {
    isAvatarUpdating: false,
    showAvatarModal: false
  };

  static defaultProps = {
    errors: [],
    firstName: undefined,
    lastName: undefined,
    username: undefined
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { avatar } = this.props;
    const { isAvatarUpdating } = this.state;

    if (isAvatarUpdating && nextProps.avatar !== avatar) {
      this.setState({
        isAvatarUpdating: false,
        showAvatarModal: false
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { errors = AccountProfile.defaultProps.errors, getCurrentUser, location } = this.props;

    if (errors !== prevProps.errors && errors.length > 0) {
      window.scrollTo(0, 0);
    }

    const prevQuery = parseQuery(prevProps.location.search, { ignoreQueryPrefix: true });
    const currentQuery = parseQuery(location.search, { ignoreQueryPrefix: true });

    if (prevQuery.modal === 'verify-id' && currentQuery.modal !== prevQuery.modal) {
      getCurrentUser();
    }
  }

  componentWillUnmount() {
    const { onClearError } = this.props;
    onClearError();
  }

  handleAvatarChange = (imageUrl, crop) => {
    const { onUpdate } = this.props;

    this.setState(
      {
        isAvatarUpdating: true
      },
      () =>
        onUpdate({
          avatar: {
            image: imageUrl,
            crop
          }
        })
    );
  };

  handleAvatarRemove = () => {
    const { onUpdate } = this.props;
    onUpdate({
      avatar: null
    });
  };

  openAvatarModal = () => {
    this.setState({
      showAvatarModal: true
    });
  };

  closeAvatarModal = () => {
    const { onClearError } = this.props;
    this.setState(
      {
        showAvatarModal: false
      },
      onClearError
    );
  };

  render() {
    const {
      avatar,
      darkMode,
      emailVerified,
      enableMobileVerification,
      errors = AccountProfile.defaultProps.errors,
      firstName,
      identityVerified,
      lastName,
      phoneVerified,
      profileCompleted,
      updatingUser,
      userId,
      username
    } = this.props;

    const { showAvatarModal } = this.state;

    const steps = {
      phone: phoneVerified,
      email: emailVerified,
      identity: identityVerified,
      profile: profileCompleted
    };
    if (!enableMobileVerification) {
      delete steps.phone;
    }

    const total = keys(steps).length;
    const numCompleted = flow(
      pickBy(identity),
      keys,
      size
    )(steps);

    return (
      <div>
        <Helmet title={t('Profile')} />
        <NotificationHandler
          type="success"
          afterCond={!updatingUser && errors.length === 0}
          beforeCond={updatingUser}
        >
          {t('Your profile has been updated')}
        </NotificationHandler>
        <ProfileMain>
          <InfoPane>
            <InfoTop>
              <AvatarContainer>
                <Tappable component={AvatarBox} onTap={this.openAvatarModal}>
                  <Responsive desktop>
                    {isDesktop => <Avatar avatar={avatar} size={isDesktop ? 'large' : 'medium'} />}
                  </Responsive>
                </Tappable>
              </AvatarContainer>
              <UserDetails>
                <NameDisplay>
                  {firstName} {lastName}
                </NameDisplay>
                <UsernameDisplay darkMode={darkMode}>@{username}</UsernameDisplay>
                <UsernameDisplay darkMode={darkMode}>Client Account Id #{userId}</UsernameDisplay>
                <StyledNightModeButton selected={darkMode} />
              </UserDetails>
              <VerificationHeadlineWrapper>
                <VerificationHeadlineInner>
                  <VerificationHeadline
                    darkMode={darkMode}
                    total={total}
                    numCompleted={numCompleted}
                  />
                </VerificationHeadlineInner>
              </VerificationHeadlineWrapper>
            </InfoTop>
            <VerificationSummaryWrapper>
              <VerificationSummary
                darkMode={darkMode}
                enableMobileVerification={enableMobileVerification}
                emailVerificationStatus={emailVerified ? 'done' : 'not done'}
                identityVerificationStatus={identityVerified ? 'done' : 'not done'}
                phoneVerificationStatus={phoneVerified ? 'done' : 'not done'}
                profileCompletionStatus={profileCompleted ? 'done' : 'not done'}
              />
            </VerificationSummaryWrapper>
            <Responsive desktop={!identityVerified}>
              <Bottom>
                {!profileCompleted && (
                  <WarningMessage darkMode={darkMode}>
                    You must complete your profile before verifying your id
                  </WarningMessage>
                )}
                <Button disabled={!profileCompleted} block link={{ search: '?modal=verify-id' }}>
                  {t('Verify ID')}
                </Button>
              </Bottom>
            </Responsive>
          </InfoPane>
          <FormPane>
            {errors.length > 0 && <FormValidationMessage message={errors} />}
            <ProfileForm />
          </FormPane>
        </ProfileMain>
        {showAvatarModal && (
          <Modal size="small" onCancel={this.closeAvatarModal} isOpen>
            <AvatarChooser
              currentAvatar={avatar}
              onClose={this.closeAvatarModal}
              onRemove={this.handleAvatarRemove}
              onSave={this.handleAvatarChange}
              updatingUser={updatingUser}
              userUpdateErrorMessage={errors}
            />
          </Modal>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    avatar: get('avatar', state.user.currentUser),
    darkMode: state.settings.darkMode,
    emailVerified: state.user.currentUser.email_verified,
    enableMobileVerification: state.settings.enableMobileVerification,
    errors: state.user.errors,
    firstName: state.user.currentUser.first_name,
    identityVerified: state.user.currentUser.id_verified,
    isSignedIn: state.user.isSignedIn,
    lastName: state.user.currentUser.last_name,
    phoneVerified: state.user.currentUser.phone_verified,
    profileCompleted: state.user.currentUser.profile_completed,
    updatingUser: state.user.updatingUser,
    userId: state.user.currentUser.id,
    username: state.user.currentUser.name
  };
}

const actions = {
  onClearError: userActions.resetUserErrors,
  onUpdate: userActions.updateUserData,
  getCurrentUser: userActions.getCurrentUser
};

const connector = compose(
  withRouter,
  connect(
    mapStateToProps,
    actions
  )
);

export default connector(AccountProfile);
